// Passos 1–4 do assistente: Objetivo, Público, Anúncio e Orçamento.
import { useId, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Check, MapPin, Plus, Sparkles, X } from 'lucide-react'
import type { Goal } from '@impulso/shared'
import { brl } from '@impulso/shared'
import { BUILDER_LIMITS as L, splitBudget, type BlueprintDto, type BuilderOptionsResponse, type BuilderPlatform } from '@impulso/shared/api/builder'
import AdCopyGenerator from '../ai/AdCopyGenerator'
import { paymentGuideFor } from '../connections/paymentGuides'
import ImagePicker from '../assets/ImagePicker'
import { Field, PlatformLogo, Switch, Tabs } from '../../ui'
import MetaPostPicker from './MetaPostPicker'
import { GOALS, isoDay, suggestionsFor } from './constants'

export type Update = (fn: (b: BlueprintDto) => BlueprintDto) => void

export interface StepProps {
  bp: BlueprintDto
  update: Update
  options: BuilderOptionsResponse | undefined
  companyId: string
}

export function usable(options: BuilderOptionsResponse | undefined, p: BuilderPlatform) {
  return !!options?.platforms.find((x) => x.platform === p)?.usable
}

/** Plataformas que recebem a campanha com a divisão atual (para limites e campos condicionais). */
export function targetPlatforms(bp: BlueprintDto, options: BuilderOptionsResponse | undefined): BuilderPlatform[] {
  const s = splitBudget(bp.budget.dailyBudget, bp.budget.split, { meta: usable(options, 'meta'), google_ads: usable(options, 'google_ads'), tiktok: usable(options, 'tiktok'), openai_ads: usable(options, 'openai_ads') })
  const t = (Object.keys(s) as BuilderPlatform[])
  if (t.length) return t
  return bp.budget.split === 'google' ? ['google_ads'] : bp.budget.split === 'meta' ? ['meta'] : bp.budget.split === 'tiktok' ? ['tiktok'] : bp.budget.split === 'openai' ? ['openai_ads'] : ['meta', 'google_ads']
}

/** Bloco com vários controles (ou nenhum campo único): o rótulo nomeia o grupo para o leitor de tela. Campo único usa o Field de ui/. */
function Group({ label, children, hint }: { label: ReactNode; children: ReactNode; hint?: ReactNode }) {
  const id = useId()
  return (
    <div className="field" role="group" aria-labelledby={id}>
      <label id={id}>{label}</label>
      {children}
      {hint && <p className="small muted">{hint}</p>}
    </div>
  )
}

function Counter({ value, max, label, soft }: { value: string; max: number; label: string; soft?: boolean }) {
  const over = value.length > max
  return <span className="small" style={{ color: over ? (soft ? 'var(--amber)' : 'var(--red)') : 'var(--muted)' }}>{label}: {value.length}/{max}</span>
}

function ChipEditor({ items, suggestions, onChange, placeholder, max = 30 }: { items: string[]; suggestions: string[]; onChange: (v: string[]) => void; placeholder: string; max?: number }) {
  const [text, setText] = useState('')
  const all = [...new Set([...suggestions, ...items])]
  const add = () => {
    const v = text.trim().slice(0, 80)
    if (v && !items.includes(v) && items.length < max) onChange([...items, v])
    setText('')
  }
  return (
    <div className="stack" style={{ gap: 10 }}>
      <div className="row wrap" style={{ gap: 8 }}>
        {all.map((i) => {
          const on = items.includes(i)
          return (
            <button type="button" key={i} className={`chip ${on ? 'on' : ''}`} aria-pressed={on} onClick={() => onChange(on ? items.filter((x) => x !== i) : [...items, i])}>
              {on && <Check size={14} />} {i}
            </button>
          )
        })}
      </div>
      <div className="row" style={{ gap: 8 }}>
        <input className="input" value={text} placeholder={placeholder} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add() } }} style={{ flex: 1, minWidth: 0 }} />
        <button type="button" className="btn sm" onClick={add} disabled={!text.trim()}><Plus size={14} /> Adicionar</button>
      </div>
    </div>
  )
}

// ---------- 1. Objetivo ----------
export function GoalStep({ bp, update, options }: StepProps) {
  const setGoal = (goal: Goal) => update((b) => ({ ...b, goal }))
  const dest = bp.destination ?? {}
  const setDest = (k: 'whatsappNumber' | 'url' | 'phone', v: string) => update((b) => ({ ...b, destination: { ...(b.destination ?? {}), [k]: v || null } }))
  return (
    <div className="stack">
      <div>
        <h2>O que você quer que aconteça?</h2>
        <p className="muted mt-8">Escolha o resultado que mais importa para o seu negócio agora.</p>
      </div>
      {GOALS.map((g) => (
        <button type="button" key={g.id} className={`option-card ${bp.goal === g.id ? 'on' : ''}`} onClick={() => setGoal(g.id)} aria-pressed={bp.goal === g.id}>
          <span className="ic purple"><g.icon size={18} /></span>
          <span>
            <span className="strong" style={{ display: 'block' }}>{g.title}</span>
            <span className="small muted">{g.desc}</span>
            {options?.goal === g.id && <span className="badge purple" style={{ marginTop: 8 }}>Seu objetivo principal</span>}
          </span>
          <span className="radio" />
        </button>
      ))}
      {bp.goal === 'whatsapp' && (
        <Field label="Número do WhatsApp que vai receber as mensagens" hint="Com DDD. Ex.: (31) 99999-0000">
          <input className="input" inputMode="tel" value={dest.whatsappNumber ?? ''} onChange={(e) => setDest('whatsappNumber', e.target.value)} placeholder="(00) 00000-0000" />
        </Field>
      )}
      {bp.goal !== 'whatsapp' && (
        <Field label={bp.goal === 'site' ? 'Endereço do site ou da página do produto' : bp.goal === 'leads' ? 'Endereço da página de cadastro' : 'Site do negócio (o Google pede um)'} hint={options?.website && !dest.url ? `Se deixar em branco, usamos ${options.website}` : undefined}>
          <input className="input" inputMode="url" value={dest.url ?? ''} onChange={(e) => setDest('url', e.target.value)} placeholder={options?.website ?? 'https://'} />
        </Field>
      )}
      {bp.goal === 'calls' && (
        <Field label="Telefone que vai receber as ligações" hint="Com DDD.">
          <input className="input" inputMode="tel" value={dest.phone ?? ''} onChange={(e) => setDest('phone', e.target.value)} placeholder="(00) 0000-0000" />
        </Field>
      )}
    </div>
  )
}

// ---------- 2. Público ----------
export function AudienceStep({ bp, update, options }: StepProps) {
  const a = bp.audience
  const setA = (patch: Partial<BlueprintDto['audience']>) => update((b) => ({ ...b, audience: { ...b.audience, ...patch } }))
  const sug = suggestionsFor(options?.segment)
  const googleOn = targetPlatforms(bp, options).includes('google_ads')
  const circle = Math.min(170, 24 + Math.sqrt(a.radiusKm) * 18)
  return (
    <div className="stack" style={{ gap: 20 }}>
      <div>
        <h2>Quem você quer alcançar?</h2>
        <p className="muted mt-8">Já sugerimos um público com base no seu tipo de negócio. Ajuste o que quiser.</p>
      </div>

      <Group label="Onde estão seus clientes">
        <div className="row wrap" style={{ gap: 8 }}>
          {([[false, 'Perto do meu negócio', 'Um raio em volta da sua cidade'], [true, 'Brasil todo', 'Para quem vende pela internet']] as const).map(([nw, title, desc]) => (
            <button type="button" key={title} className={`option-card ${!!a.nationwide === nw ? 'on' : ''}`} style={{ flex: '1 1 200px' }} onClick={() => setA({ nationwide: nw })} aria-pressed={!!a.nationwide === nw}>
              <div><div className="strong">{title}</div><div className="small muted">{desc}</div></div>
            </button>
          ))}
        </div>
        {!a.nationwide && (<>
          <div style={{ position: 'relative', height: 180, borderRadius: 12, overflow: 'hidden', background: 'repeating-linear-gradient(45deg,#eef2f7 0 12px,#e7ecf3 12px 24px)', border: '1px solid var(--border)' }} aria-hidden>
            <div style={{ position: 'absolute', left: '50%', top: '50%', width: circle, height: circle, transform: 'translate(-50%,-50%)', borderRadius: '50%', background: 'rgba(91,61,245,.15)', border: '2px solid var(--primary)', transition: 'all .15s' }} />
            <MapPin size={26} color="var(--primary)" style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-90%)' }} />
            <span className="badge" style={{ position: 'absolute', left: 10, top: 10, background: 'white', maxWidth: 'calc(100% - 20px)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {options?.companyName ?? 'Seu negócio'}{options?.city ? ` · ${options.city}` : ''}
            </span>
          </div>
          <div className="row mt-8">
            <span className="small muted nowrap">Até {a.radiusKm} km</span>
            <input type="range" className="range" aria-label="Raio em km" min={L.radiusMin} max={L.radiusMax} value={a.radiusKm} onChange={(e) => setA({ radiusKm: +e.target.value })} />
          </div>
          {!options?.city && <p className="small muted">Cadastre a cidade do negócio nas configurações para o raio ficar certinho.</p>}
        </>)}
      </Group>

      <Group label="Idade">
        <div className="row wrap">
          <input className="input" type="number" aria-label="Idade mínima" min={L.ageMin} max={L.ageMax} value={a.ageMin} onChange={(e) => setA({ ageMin: Math.round(+e.target.value) })} style={{ width: 90 }} />
          <span className="muted">até</span>
          <input className="input" type="number" aria-label="Idade máxima" min={L.ageMin} max={L.ageMax} value={a.ageMax} onChange={(e) => setA({ ageMax: Math.round(+e.target.value) })} style={{ width: 90 }} />
          <span className="muted">anos</span>
        </div>
        {(a.ageMin < L.ageMin || a.ageMax > L.ageMax || a.ageMin > a.ageMax) && <p className="small" style={{ color: 'var(--red)' }}>Use idades entre {L.ageMin} e {L.ageMax} anos.</p>}
      </Group>

      <Group label={<>Interesses <span className="badge purple" style={{ marginLeft: 6 }}><Sparkles size={11} /> sugeridos</span></>}>
        <ChipEditor items={a.interests} suggestions={sug.interests} onChange={(interests) => setA({ interests })} placeholder="Outro interesse" />
        <p className="small muted mt-8">Valem para o Instagram e o Facebook. No Google, quem decide é o que a pessoa pesquisa.</p>
      </Group>

      <div className="row" style={{ alignItems: 'flex-start', gap: 12 }}>
        <Switch on={a.advantageAudience} onChange={(v) => setA({ advantageAudience: v })} label="Deixar as plataformas ampliarem o público" />
        <div>
          <div className="strong">Deixar Instagram e Google encontrarem pessoas parecidas (recomendado)</div>
          <div className="small muted">Eles podem mostrar o anúncio para quem está fora desses interesses, mas tem grande chance de comprar.</div>
        </div>
      </div>

      {googleOn && (
        <Group label="O que as pessoas pesquisam no Google" hint="Palavras-chave: seu anúncio aparece quando alguém busca por elas.">
          <ChipEditor items={a.keywords ?? []} suggestions={sug.keywords} onChange={(keywords) => setA({ keywords })} placeholder="Ex.: bolo de aniversário" max={50} />
        </Group>
      )}
    </div>
  )
}

// ---------- 3. Anúncio ----------
export function AdStep({ bp, update, options, companyId, previewPlatform, canEdit }: StepProps & { previewPlatform: BuilderPlatform; canEdit: boolean }) {
  const c = bp.creative
  const targets = targetPlatforms(bp, options)
  const setC = (patch: Partial<BlueprintDto['creative']>) => update((b) => ({ ...b, creative: { ...b.creative, ...patch } }))
  const onPick = (v: { headline: string; body: string }) => update((b) => {
    const prev = b.creative
    const alts = [...(prev.alternatives ?? [])]
    if (prev.headline.trim() && (prev.headline !== v.headline || prev.body !== v.body) && !alts.some((x) => x.headline === prev.headline && x.body === prev.body)) {
      alts.unshift({ headline: prev.headline, body: prev.body })
    }
    return { ...b, creative: { ...prev, headline: v.headline, body: v.body, alternatives: alts.filter((x) => x.headline !== v.headline || x.body !== v.body).slice(0, 4) } }
  })
  // O mesmo texto vai para todas as plataformas escolhidas: com Google na divisão, gera nos limites dele (mais curtos),
  // assim a variação escolhida passa na validação das duas.
  // A IA ainda não tem modo TikTok nem ChatGPT: usa o do Instagram (a validação avisa se o texto passar de 100 caracteres).
  const genPlatform = targets.includes('google_ads') ? 'google_ads' : 'meta'
  // Post da Meta: só o Instagram/Facebook usa o post; as outras plataformas continuam com título, texto e imagem.
  const [usePost, setUsePost] = useState(!!c.metaPost)
  const postMode = targets.includes('meta') && usePost
  const postOnly = postMode && !!c.metaPost && targets.every((p) => p === 'meta')
  const othersNeedImage = targets.some((p) => p === 'tiktok' || p === 'openai_ads')
  const setMode = (v: 'image' | 'post') => { setUsePost(v === 'post'); if (v === 'image' && c.metaPost) setC({ metaPost: null }) }
  return (
    <div className="stack" style={{ gap: 20 }}>
      <div>
        <h2>O que você quer divulgar?</h2>
        <p className="muted mt-8">Escreva do seu jeito. A IA transforma em textos de anúncio.</p>
      </div>
      <Field label="Produto ou oferta">
        <textarea className="textarea" value={bp.offer} maxLength={1000} onChange={(e) => update((b) => ({ ...b, offer: e.target.value }))} placeholder="Ex.: Bolos temáticos para o Dia das Crianças, encomendas até 10/10" />
      </Field>

      {!postOnly && bp.offer.trim().length >= 3 && (
        <AdCopyGenerator
          companyId={companyId}
          brief={bp.offer}
          platform={genPlatform}
          format={genPlatform === 'google_ads' ? 'search' : 'feed'}
          goal={bp.goal}
          onPick={onPick}
        />
      )}

      {!postOnly && <>
      <Field label="Título" hint={(
        <span className="row wrap" style={{ gap: 12 }}>
          {targets.includes('meta') && <Counter value={c.headline} max={L.metaHeadlineMax} label="Instagram" />}
          {targets.includes('google_ads') && <Counter value={c.headline} max={L.googleHeadlineMax} label="Google" />}
          {targets.includes('tiktok') && <span className="small muted">TikTok: o título não aparece no anúncio</span>}
          {targets.includes('openai_ads') && <Counter value={c.headline} max={L.openaiTitleMax} label="ChatGPT" />}
        </span>
      )}>
        <input className="input" value={c.headline} maxLength={300} onChange={(e) => setC({ headline: e.target.value })} placeholder="Ex.: Bolos artesanais em BH" />
      </Field>
      <Field label="Texto" hint={(
        <span className="row wrap" style={{ gap: 12 }}>
          {targets.includes('meta') && <Counter value={c.body} max={L.metaBodyRecommended} label="Instagram (recomendado)" soft />}
          {targets.includes('google_ads') && <Counter value={c.body} max={L.googleDescriptionMax} label="Google" />}
          {targets.includes('tiktok') && <Counter value={c.body} max={L.tiktokBodyMax} label="TikTok" />}
          {targets.includes('openai_ads') && <Counter value={c.body} max={L.openaiBodyMax} label="ChatGPT" />}
        </span>
      )}>
        <textarea className="textarea" value={c.body} maxLength={2000} onChange={(e) => setC({ body: e.target.value })} placeholder="Conte o que você oferece e chame para a ação." />
      </Field>

      {(c.alternatives?.length ?? 0) > 0 && (
        <div className="stack" style={{ gap: 8 }}>
          <label className="small strong muted">OUTRAS VERSÕES (as plataformas testam e mostram a que funcionar melhor)</label>
          {c.alternatives!.map((alt, i) => (
            <div key={i} className="option-card" style={{ padding: 12, cursor: 'default' }}>
              <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
                <span className="strong" style={{ display: 'block' }}>{alt.headline}</span>
                <span className="small muted">{alt.body}</span>
              </span>
              <button type="button" className="btn ghost sm" aria-label="Remover versão" onClick={() => setC({ alternatives: c.alternatives!.filter((_, j) => j !== i) })}><X size={14} /></button>
            </div>
          ))}
        </div>
      )}
      </>}

      {targets.includes('meta') && (
        <Group label="Criativo do Instagram/Facebook" hint={postMode && targets.some((p) => p !== 'meta') ? 'O título, o texto e a imagem acima continuam valendo para as outras plataformas.' : undefined}>
          <Tabs value={postMode ? 'post' : 'image'} onChange={setMode} options={[{ value: 'image', label: 'Imagem e texto' }, { value: 'post', label: 'Usar um post' }]} />
          {postMode && <MetaPostPicker companyId={companyId} value={c.metaPost} onChange={(metaPost) => setC({ metaPost })} />}
        </Group>
      )}

      {(!postMode || othersNeedImage) && <Group label={<>Imagem {(targets.includes('meta') && !postMode) || targets.includes('tiktok') || targets.includes('openai_ads') ? <span className="small muted">(obrigatória no {targets.includes('meta') && !postMode ? 'Instagram/Facebook' : targets.includes('tiktok') ? 'TikTok' : 'ChatGPT'})</span> : <span className="small muted">(o Google Pesquisa não usa imagem)</span>}</>}>
        <ImagePicker companyId={companyId} value={c.imageAssetId} onChange={(imageAssetId) => setC({ imageAssetId })} canEdit={canEdit} />
      </Group>}
    </div>
  )
}

// ---------- 4. Orçamento ----------
export function BudgetStep({ bp, update, options, companyId }: StepProps) {
  const b = bp.budget
  const setB = (patch: Partial<BlueprintDto['budget']>) => update((x) => ({ ...x, budget: { ...x.budget, ...patch } }))
  const metaOk = usable(options, 'meta')
  const googleOk = usable(options, 'google_ads')
  const tiktokOk = usable(options, 'tiktok')
  const openaiOk = usable(options, 'openai_ads')
  const shares = splitBudget(b.dailyBudget, 'auto', { meta: metaOk, google_ads: googleOk })
  const both = metaOk && googleOk
  const until = !!b.endDate
  const platformInfo = (p: BuilderPlatform) => options?.platforms.find((x) => x.platform === p)

  const notConnected = (p: BuilderPlatform) => {
    const info = platformInfo(p)
    return (
      <span className="small" style={{ display: 'block', marginTop: 6 }}>
        <span className="badge amber">{info?.connected ? (info.problem ?? 'Precisa reconectar') : 'Não conectado'}</span>{' '}
        {info?.problem === 'Sem forma de pagamento' && paymentGuideFor(p)
          ? <Link to={`/app/${companyId}/contas/pagamento?plataforma=${p}`} className="strong" style={{ color: 'var(--primary)' }} onClick={(e) => e.stopPropagation()}>Ver como pagar</Link>
          : <Link to={`/app/${companyId}/contas`} className="strong" style={{ color: 'var(--primary)' }} onClick={(e) => e.stopPropagation()}>{info?.connected ? 'Reconectar' : 'Conectar conta'}</Link>}
      </span>
    )
  }

  return (
    <div className="stack" style={{ gap: 20 }}>
      <div>
        <h2>Quanto você quer investir?</h2>
        <p className="muted mt-8">Você paga direto à plataforma onde anunciar, no cartão cadastrado lá. O Seu Impulso nunca passa do limite que você definir.</p>
      </div>

      <Group label="Por dia">
        <div className="row wrap" style={{ gap: 16 }}>
          <span style={{ fontSize: 30, fontWeight: 700, minWidth: 110 }}>{brl(b.dailyBudget)}</span>
          <input type="range" className="range" aria-label="Orçamento por dia" min={5} max={300} step={1} value={Math.min(300, b.dailyBudget)} onChange={(e) => setB({ dailyBudget: +e.target.value })} style={{ flex: 1, minWidth: 160 }} />
          <input className="input" type="number" aria-label="Valor por dia em reais" min={5} max={100000} value={b.dailyBudget} onChange={(e) => setB({ dailyBudget: Math.max(0, +e.target.value) })} style={{ width: 100 }} />
        </div>
        <div className="row between small muted"><span>R$ 5</span><span className="hide-xs">Mínimo de R$ 5 por dia em cada plataforma (TikTok: R$ {L.tiktokMinDaily})</span><span>R$ 300</span></div>
      </Group>

      <Group label="Por quanto tempo">
        <div className="row wrap" style={{ gap: 8 }}>
          <button type="button" className={`chip ${!until ? 'on' : ''}`} onClick={() => setB({ endDate: null })}>Sem data para acabar</button>
          <button type="button" className={`chip ${until ? 'on' : ''}`} onClick={() => setB({ endDate: b.endDate ?? isoDay(14) })}>Até uma data</button>
          {until && <input className="input" type="date" aria-label="Data final" min={isoDay(1)} value={b.endDate ?? ''} onChange={(e) => setB({ endDate: e.target.value || null })} style={{ width: 170 }} />}
        </div>
      </Group>

      <Group label="Onde anunciar">
        <button type="button" className={`option-card ${b.split === 'auto' ? 'on' : ''}`} onClick={() => setB({ split: 'auto' })} disabled={!metaOk && !googleOk}>
          <span className="ic purple"><Sparkles size={18} /></span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span className="strong" style={{ display: 'block' }}>Deixar o Seu Impulso dividir (recomendado)</span>
            <span className="small muted">
              {both ? 'Começa com 70% no Instagram/Facebook e 30% no Google. Toda semana move o dinheiro para onde os clientes custarem menos.'
                : metaOk ? 'Só o Instagram/Facebook está conectado: 100% vai para lá. Conecte o Google para dividir.'
                : googleOk ? 'Só o Google está conectado: 100% vai para lá. Conecte o Instagram/Facebook para dividir.'
                : 'Conecte pelo menos uma conta de anúncios para publicar.'}
            </span>
            {(metaOk || googleOk) && (
              <>
                <span className="progress mt-12" style={{ display: 'flex' }}>
                  {shares.meta !== undefined && <span style={{ width: `${(shares.meta / Math.max(b.dailyBudget, 0.01)) * 100}%`, background: 'var(--meta)', borderRadius: 0 }} />}
                  {shares.google_ads !== undefined && <span style={{ width: `${(shares.google_ads / Math.max(b.dailyBudget, 0.01)) * 100}%`, background: '#34a853', borderRadius: 0 }} />}
                </span>
                <span className="row wrap small mt-8" style={{ gap: 14 }}>
                  {shares.meta !== undefined && <span className="row" style={{ gap: 6 }}><PlatformLogo platform="meta" /> {brl(shares.meta, 2)}/dia</span>}
                  {shares.google_ads !== undefined && <span className="row" style={{ gap: 6 }}><PlatformLogo platform="google" /> {brl(shares.google_ads, 2)}/dia</span>}
                </span>
              </>
            )}
          </span>
          <span className="radio" />
        </button>
        <button type="button" className={`option-card ${b.split === 'meta' ? 'on' : ''}`} onClick={() => metaOk && setB({ split: 'meta' })} aria-disabled={!metaOk} style={!metaOk ? { opacity: 0.75 } : undefined}>
          <PlatformLogo platform="meta" size="lg" />
          <span style={{ flex: 1 }}>
            <span className="strong" style={{ display: 'block' }}>Só Instagram e Facebook</span>
            <span className="small muted">Bom para mostrar fotos dos produtos e criar desejo.</span>
            {!metaOk && notConnected('meta')}
          </span>
          <span className="radio" />
        </button>
        <button type="button" className={`option-card ${b.split === 'google' ? 'on' : ''}`} onClick={() => googleOk && setB({ split: 'google' })} aria-disabled={!googleOk} style={!googleOk ? { opacity: 0.75 } : undefined}>
          <PlatformLogo platform="google" size="lg" />
          <span style={{ flex: 1 }}>
            <span className="strong" style={{ display: 'block' }}>Só Google</span>
            <span className="small muted">Bom para quem já está procurando o que você vende.</span>
            {!googleOk && notConnected('google_ads')}
          </span>
          <span className="radio" />
        </button>
        {platformInfo('tiktok')?.connected && (
          <button type="button" className={`option-card ${b.split === 'tiktok' ? 'on' : ''}`} onClick={() => tiktokOk && setB({ split: 'tiktok' })} aria-disabled={!tiktokOk} style={!tiktokOk ? { opacity: 0.75 } : undefined}>
            <PlatformLogo platform="tiktok" size="lg" />
            <span style={{ flex: 1 }}>
              <span className="strong" style={{ display: 'block' }}>Só TikTok</span>
              <span className="small muted">Mínimo de {brl(L.tiktokMinDaily)} por dia. Com imagem o anúncio aparece nos apps parceiros do TikTok, não no feed de vídeos.</span>
              {!tiktokOk && notConnected('tiktok')}
            </span>
            <span className="radio" />
          </button>
        )}
        {platformInfo('openai_ads')?.connected && (
          <button type="button" className={`option-card ${b.split === 'openai' ? 'on' : ''}`} onClick={() => openaiOk && setB({ split: 'openai' })} aria-disabled={!openaiOk} style={!openaiOk ? { opacity: 0.75 } : undefined}>
            <PlatformLogo platform="openai_ads" size="lg" />
            <span style={{ flex: 1 }}>
              <span className="strong" style={{ display: 'block' }}>Só ChatGPT</span>
              <span className="small muted">Anúncio com imagem dentro das conversas do ChatGPT. Mostramos para a cidade da empresa, sem raio nem idade.</span>
              {!openaiOk && notConnected('openai_ads')}
            </span>
            <span className="radio" />
          </button>
        )}
        {options?.platforms.some((p) => p.demo) && <p className="small muted">Modo demonstração: a conta de teste faz o papel das plataformas.</p>}
      </Group>
    </div>
  )
}
